/**
 * BC Scratch Popup
 *
 * Custom scratch-to-reveal shell around Klaviyo *embedded* forms. Klaviyo owns the
 * inputs, consent handling and profile creation; this file owns the modal, the
 * canvas scratch interaction, and moving between steps.
 *
 * Step advancement listens to Klaviyo's `klaviyoForms` window event rather than
 * intercepting submits, so double opt-in and validation stay Klaviyo's business.
 */

const STEP_ORDER = ['scratch', 'email', 'sms', 'success'];

class BCScratchPopup extends HTMLElement {
  connectedCallback() {
    if (this.mounted) return;
    this.mounted = true;

    this.config = this.readConfig();

    this.dialog = this.querySelector('.bcsp__dialog');
    this.steps = new Map();
    for (const el of this.querySelectorAll('[data-bcsp-step]')) {
      this.steps.set(el.dataset.bcspStep, el);
    }

    this.scratchEl = this.querySelector('[data-bcsp-scratch]');
    this.canvas = this.querySelector('[data-bcsp-canvas]');

    this.observers = [];
    for (const [, el] of this.steps) {
      el.inert = el.dataset.active !== 'true';
    }

    this.watchEmbedSubmit('email', () => this.advanceFromEmail());
    this.watchEmbedSubmit('sms', () => this.goTo('success'));

    this.onKeydown = this.onKeydown.bind(this);
    this.onKlaviyoEvent = this.onKlaviyoEvent.bind(this);

    this.bindChrome();
    this.setupScratch();

    window.addEventListener('klaviyoForms', this.onKlaviyoEvent);

    if (window.Shopify?.designMode) {
      // In the theme editor, preview on select instead of waiting for a trigger.
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail?.sectionId && this.id.includes(event.detail.sectionId.replace(/[_-]/g, ''))) this.open();
      });
      document.addEventListener('shopify:section:deselect', () => this.close({ remember: false }));
      return;
    }

    this.armTriggers();
  }

  disconnectedCallback() {
    window.removeEventListener('klaviyoForms', this.onKlaviyoEvent);
    document.removeEventListener('keydown', this.onKeydown);
    clearTimeout(this.delayTimer);
    for (const observer of this.observers ?? []) observer.disconnect();
  }

  readConfig() {
    const node = this.querySelector('[data-bcsp-config]');
    try {
      return JSON.parse(node?.textContent ?? '{}');
    } catch {
      return {};
    }
  }

  /* ---------- storage ---------- */

  get storageKey() {
    return this.config.storageKey || 'bcsp';
  }

  isSuppressed() {
    const days = Number(this.config.frequencyDays ?? 0);
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return false;
      const { at, done } = JSON.parse(raw);
      if (done) return true;
      if (days <= 0) return false;
      return Date.now() - at < days * 864e5;
    } catch {
      return false;
    }
  }

  remember(done = false) {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ at: Date.now(), done }));
    } catch {
      /* private mode — fall back to showing again next visit */
    }
  }

  /* ---------- triggers ---------- */

  armTriggers() {
    // A #bc-scratch link or [data-bc-scratch-open] button always works, even in manual mode.
    document.addEventListener('click', (event) => {
      const opener = event.target.closest?.('[data-bc-scratch-open], a[href="#bc-scratch"]');
      if (!opener) return;
      event.preventDefault();
      this.open();
    });

    if (this.isSuppressed()) return;

    const trigger = this.config.trigger || 'delay';
    const delayMs = Number(this.config.delaySeconds ?? 5) * 1000;

    if (trigger === 'manual') return;

    if (trigger === 'delay') {
      this.delayTimer = setTimeout(() => this.open(), delayMs);
      return;
    }

    if (trigger === 'scroll') {
      const threshold = Number(this.config.scrollPercent ?? 35) / 100;
      const onScroll = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0 || window.scrollY / max >= threshold) {
          window.removeEventListener('scroll', onScroll);
          this.open();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return;
    }

    if (trigger === 'exit') {
      const finePointer = window.matchMedia('(pointer: fine)').matches;
      if (finePointer) {
        const onLeave = (event) => {
          if (event.clientY > 0) return;
          document.removeEventListener('mouseout', onLeave);
          this.open();
        };
        document.addEventListener('mouseout', onLeave);
      } else {
        // No exit intent on touch — fall back to the delay so mobile still converts.
        this.delayTimer = setTimeout(() => this.open(), delayMs || 8000);
      }
    }
  }

  /* ---------- open / close ---------- */

  bindChrome() {
    for (const el of this.querySelectorAll('[data-bcsp-close]')) {
      el.addEventListener('click', (event) => {
        // The success CTA is a real link — let it navigate, just record the close.
        if (el.tagName !== 'A') event.preventDefault();
        this.close();
      });
    }

    const next = this.querySelector('[data-bcsp-next]');
    next?.addEventListener('click', () => {
      this.revealScratch();
      this.goTo('email');
    });

    this.bindCopy();
  }

  /* ---------- discount code ---------- */

  bindCopy() {
    // The whole coupon row is the target, so tapping the code works as well as
    // the button — the button just bubbles up to the same handler.
    const box = this.querySelector('[data-bcsp-code-box]');
    if (!box) return;

    box.addEventListener('click', () => this.copyCode(box));
  }

  async copyCode(box) {
    const code = this.querySelector('[data-bcsp-code]')?.textContent?.trim();
    if (!code) return;

    const button = box.querySelector('[data-bcsp-copy]');
    const label = box.querySelector('[data-bcsp-copy-label]') || button;
    const status = this.querySelector('[data-bcsp-copy-status]');
    const copied = await this.writeClipboard(code);

    // A blocked clipboard still gets feedback: the code is selected so the
    // visitor can copy it by hand, and the message says so.
    if (!copied) this.selectCode();

    if (button) button.dataset.copied = copied ? 'true' : 'false';
    if (label && copied) label.textContent = button?.dataset.copiedLabel || 'Copied';

    if (status) {
      status.textContent = copied
        ? status.dataset.copiedMessage || 'Code copied to clipboard'
        : status.dataset.failedMessage || 'Press Ctrl+C to copy the code';
      status.dataset.visible = 'true';
      status.dataset.state = copied ? 'ok' : 'manual';
    }

    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      if (button) {
        button.dataset.copied = 'false';
        if (label) label.textContent = button.dataset.copyLabel || 'Copy';
      }
      if (status) status.dataset.visible = 'false';
    }, 2600);
  }

  /**
   * navigator.clipboard is unavailable on insecure origins and is blocked
   * inside the theme editor's iframe, so fall back to a throwaway textarea.
   */
  async writeClipboard(text) {
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* fall through to the legacy path */
    }

    try {
      const field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
      this.appendChild(field);
      field.select();
      field.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      field.remove();
      return ok;
    } catch {
      return false;
    }
  }

  selectCode() {
    const value = this.querySelector('[data-bcsp-code]');
    if (!value) return;
    try {
      const range = document.createRange();
      range.selectNodeContents(value);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    } catch {
      /* selection is a nicety, not a requirement */
    }
  }

  open() {
    if (this.dataset.open === 'true') return;
    this.dataset.open = 'true';
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeydown);

    // Klaviyo sizes its embed on layout; nudge it now that the modal has real dimensions.
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      this.sizeCanvas();
    });

    this.dialog?.querySelector('button, [href], input')?.focus?.({ preventScroll: true });
  }

  close({ remember = true } = {}) {
    if (this.dataset.open !== 'true') return;
    this.dataset.open = 'false';
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this.onKeydown);
    if (remember) this.remember(this.currentStep === 'success');
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  goTo(name) {
    const target = this.steps.get(name);
    if (!target) {
      // SMS step is optional — skip past anything that isn't rendered.
      const next = STEP_ORDER[STEP_ORDER.indexOf(name) + 1];
      if (next) this.goTo(next);
      return;
    }
    for (const [key, el] of this.steps) {
      const active = key === name;
      el.dataset.active = String(active);
      el.inert = !active;
    }
    this.currentStep = name;
    if (name === 'success') this.remember(true);
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  /* ---------- Klaviyo ---------- */

  onKlaviyoEvent(event) {
    const detail = event.detail || {};
    if (detail.type !== 'submit') return;

    const formId = String(detail.formId || '');
    if (formId && formId === String(this.config.emailFormId || '')) {
      this.advanceFromEmail();
    } else if (formId && formId === String(this.config.smsFormId || '')) {
      this.goTo('success');
    }
  }

  /**
   * The klaviyoForms `submit` event is the primary signal, but it does not
   * arrive on every Klaviyo form configuration. Watch the embed itself as a
   * fallback: once Klaviyo swaps its success state in, the field it rendered
   * disappears from the container.
   */
  watchEmbedSubmit(stepName, onSubmit) {
    const container = this.steps.get(stepName)?.querySelector('.bcsp__klaviyo');
    if (!container || typeof MutationObserver === 'undefined') return;

    let sawField = false;
    const hasField = () => !!container.querySelector('input[type="email"], input[type="tel"]');

    const observer = new MutationObserver(() => {
      if (hasField()) {
        sawField = true;
        return;
      }
      // Only meaningful once Klaviyo has actually mounted and then cleared it,
      // and only while this step is the one on screen.
      if (!sawField || this.currentStep !== stepName) return;
      observer.disconnect();
      onSubmit();
    });

    observer.observe(container, { childList: true, subtree: true });
    this.observers.push(observer);
    if (hasField()) sawField = true;
  }

  advanceFromEmail() {
    const smsStep = this.steps.get('sms');
    if (!smsStep) {
      this.goTo('success');
      return;
    }

    this.goTo('sms');

    // Never strand the visitor on an empty step. If Klaviyo has not mounted the
    // SMS embed by now it is not going to, so fall through to the success
    // screen rather than showing a blank panel with no way forward.
    setTimeout(() => {
      if (this.currentStep === 'sms' && !this.embedHasContent(smsStep)) this.goTo('success');
    }, 1500);
  }

  embedHasContent(step) {
    const container = step.querySelector('.bcsp__klaviyo');
    if (!container) return false;
    return container.querySelectorAll('input, button, iframe').length > 0;
  }

  /* ---------- scratch card ---------- */

  setupScratch() {
    if (!this.canvas || !this.scratchEl) return;

    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      // No canvas support — the card is already legible, the button still advances.
      this.revealScratch();
      return;
    }
    this.ctx = ctx;

    this.sizeCanvas();
    this.resizeObserver = new ResizeObserver(() => this.sizeCanvas());
    this.resizeObserver.observe(this.scratchEl);

    let drawing = false;
    let moved = false;

    const point = (event) => {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    this.canvas.addEventListener('pointerdown', (event) => {
      if (this.revealed) return;
      drawing = true;
      moved = false;
      this.canvas.setPointerCapture(event.pointerId);
      this.last = point(event);
      this.scratchAt(this.last, this.last);
    });

    this.canvas.addEventListener('pointermove', (event) => {
      if (!drawing || this.revealed) return;
      moved = true;
      const next = point(event);
      this.scratchAt(this.last, next);
      this.last = next;

      // Sampling every frame is wasteful; a few times a second reads as instant.
      const now = performance.now();
      if (now - (this.lastCheck || 0) > 220) {
        this.lastCheck = now;
        this.checkProgress();
      }
    });

    const end = () => {
      if (!drawing) return;
      drawing = false;
      // A tap with no drag counts as a reveal when the merchant opted into that.
      if (!moved && this.config.autoRevealOnClick) this.revealScratch();
      else this.checkProgress();
    };

    this.canvas.addEventListener('pointerup', end);
    this.canvas.addEventListener('pointercancel', end);
  }

  sizeCanvas() {
    if (!this.canvas || !this.ctx || this.revealed) return;
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.round(rect.width * dpr);
    const height = Math.round(rect.height * dpr);
    if (this.canvas.width === width && this.canvas.height === height) return;

    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.paintFoil(rect.width, rect.height);
  }

  paintFoil(width, height) {
    const ctx = this.ctx;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, width, height);

    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, this.config.foilTop || '#f0d98a');
    gradient.addColorStop(0.45, '#fff3c4');
    gradient.addColorStop(1, this.config.foilBottom || '#c9a227');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Diagonal sheen so the foil doesn't read as a flat swatch.
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(-width * 0.1, height);
    ctx.lineTo(width * 0.35, 0);
    ctx.lineTo(width * 0.6, 0);
    ctx.lineTo(width * 0.15, height);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    const watermark = this.config.foilWatermark;
    if (watermark) {
      ctx.save();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = '#6b5307';
      ctx.font = `900 ${Math.round(height * 0.42)}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(watermark, width / 2, height / 2);
      ctx.restore();
    }

    const label = this.config.foilLabel;
    if (label) {
      ctx.save();
      ctx.globalAlpha = 0.72;
      ctx.fillStyle = '#7a5f0b';
      ctx.font = `600 ${Math.max(13, Math.round(height * 0.11))}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label.toUpperCase(), width / 2, height / 2);
      ctx.restore();
    }
  }

  scratchAt(from, to) {
    const ctx = this.ctx;
    const size = Number(this.config.brushSize ?? 28);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(to.x, to.y, size / 2, 0, Math.PI * 2);
    ctx.fill();
  }

  checkProgress() {
    if (this.revealed) return;
    const threshold = Number(this.config.scratchThreshold ?? 45) / 100;

    // Sample every 4th pixel — plenty accurate for a percentage, far cheaper.
    const { width, height } = this.canvas;
    const data = this.ctx.getImageData(0, 0, width, height).data;
    let clear = 0;
    let total = 0;
    for (let i = 3; i < data.length; i += 16) {
      total += 1;
      if (data[i] < 32) clear += 1;
    }

    if (total > 0 && clear / total >= threshold) this.revealScratch();
  }

  revealScratch() {
    if (this.revealed) return;
    this.revealed = true;
    this.scratchEl?.setAttribute('data-revealed', 'true');
    this.steps.get('scratch')?.setAttribute('data-revealed', 'true');
    this.resizeObserver?.disconnect();
  }
}

if (!customElements.get('bc-scratch-popup')) {
  customElements.define('bc-scratch-popup', BCScratchPopup);
}
